import { useRef, useCallback, useEffect } from 'react';
import { useFetcher } from 'react-router';

type FetcherData<TData> = ReturnType<typeof useFetcher<TData>>['data'];
type ResolveFunction<T> = (value: FetcherData<T>) => void;

export function useFetcherWithPromise<TData>(opts?: Parameters<typeof useFetcher>[0]) {
  const fetcher = useFetcher<TData>(opts);
  const resolveRef = useRef<ResolveFunction<TData>>(undefined);
  const promiseRef = useRef<Promise<FetcherData<TData>>>(undefined);

  if (!promiseRef.current) {
    promiseRef.current = new Promise<FetcherData<TData>>((resolve) => {
      resolveRef.current = resolve;
    });
  }

  const resetResolver = useCallback(() => {
    promiseRef.current = new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  }, [promiseRef, resolveRef]);

  const submit = useCallback(
    async (...args: Parameters<typeof fetcher.submit>) => {
      fetcher.submit(...args);
      return promiseRef.current;
    },
    [fetcher, promiseRef],
  );

  useEffect(() => {
    if (fetcher.state === 'idle') {
      if (fetcher.data) {
        resolveRef.current?.(fetcher.data);
      }
      resetResolver();
    }
  }, [fetcher, resetResolver]);

  return { ...fetcher, submit };
}
